import React, { useState } from "react";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { safeJsonRequest } from "../../utils/safeFetch";

/**
 * NotesPanel — Internal case notes for a referral.
 */
const NotesPanel = ({ referralId, notes: initialNotes, token, onNoteAdded }) => {
  const [notes, setNotes] = useState(initialNotes || []);
  const [newNote, setNewNote] = useState("");
  const [sending, setSending] = useState(false);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSending(true);
    try {
      const data = await safeJsonRequest(`/api/admin/referrals/${referralId}/notes`, {
        method: "POST",
        body: { text: newNote.trim() },
        token,
      });
      setNotes((prev) => [...prev, data.note]);
      setNewNote("");
      if (onNoteAdded) onNoteAdded();
    } catch {
      // silent
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddNote();
    }
  };

  return (
    <div className="mt-4 p-3 bg-slate-900/60 rounded-xl border border-slate-600">
      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <MessageSquare className="w-3 h-3" /> Case Notes ({notes.length})
      </h4>

      {/* Existing notes */}
      {notes.length > 0 && (
        <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
          {notes.map((n, idx) => (
            <div key={idx} className="bg-slate-800 rounded-lg px-3 py-2 border border-slate-700" data-testid={`note-${idx}`}>
              <p className="text-xs text-slate-200 whitespace-pre-wrap">{n.text}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] text-slate-500">{n.author}</span>
                <span className="text-[10px] text-slate-600">
                  {n.created_at ? new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add note input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a case note..."
          className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-testid={`note-input-${referralId}`}
        />
        <button
          onClick={handleAddNote}
          disabled={sending || !newNote.trim()}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`note-send-${referralId}`}
        >
          {sending ? (
            <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5 text-white" />
          )}
        </button>
      </div>
    </div>
  );
};

export default NotesPanel;
